import { useToast } from '@chakra-ui/react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  createContext,
} from 'react';
import { useHistory } from 'react-router-dom';
import { v4 as uuidV4 } from 'uuid';

import { Receivable, Util } from '@finley-technologies/finley-types';

import { useGetLatestAssignmentPipelineRunStatusForLoanProductGroupingQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { useLoanProductCreditFacilityGroupContext } from './LoanProductCreditFacilityGroupingProvider';
import { errorToastConfig } from 'packages/common-frontend/constants';

export type Metadata =
  | Receivable.Assignment.Pipeline.Metadata
  | null
  | undefined; // undefined state implies initial api call has not resolved

export type Args = {
  previewIdentifier?: string;
  reportingDate?: Util.FullDateStr;
  fileVersion?: number;
};

export type LatestAssignmentPipelineRunMetadataForLpGroupingContextType = {
  metadata: Metadata;
  // not a set state dispatch because we have logic surrounding the set state
  setMetadata: (newLoanProduct: Metadata) => void;
  // setArgs: React.Dispatch<React.SetStateAction<Args>>;
};

export const LatestAssignmentPipelineRunMetadataForLpGroupingContext = createContext<LatestAssignmentPipelineRunMetadataForLpGroupingContextType | null>(
  null,
);

/**
 * # ⚠️ WARNING
 * ## To be used with `LoanProductCreditFacilityGroupContext`
 * ## Not to be used on it's own as a LP context!
 */
export const useLatestAssignmentPipelineRunMetadataForLpGroupingContext = () => {
  const context = useContext(
    LatestAssignmentPipelineRunMetadataForLpGroupingContext,
  );
  if (context === null) {
    throw new Error('LoanProductCreditFacilityGroupContext must be present');
  }

  return context;
};

export const LatestAssignmentPipelineRunMetadataForLpGroupingProvider: React.FC<{
  renderLoading: React.ReactElement;
  children: React.ReactNode;
}> = ({ renderLoading, children }) => {
  const toast = useToast();
  const history = useHistory();
  const { lpToCfEntityGrouping } = useLoanProductCreditFacilityGroupContext();
  const [metadata, setMetadata] = useState<Metadata>();

  const {
    data,
    startPolling,
    stopPolling,
  } = useGetLatestAssignmentPipelineRunStatusForLoanProductGroupingQuery({
    fetchPolicy: 'no-cache',
    variables: {
      loanProductIds:
        lpToCfEntityGrouping?.loanProducts.map(({ id }) => id) ?? [],
    },
  });

  useEffect(() => {
    startPolling(5000);
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  useEffect(() => {
    if (
      data?.latestAssignmentPipelineRunStatusForLoanProductGrouping !==
      undefined
    ) {
      setMetadata(data.latestAssignmentPipelineRunStatusForLoanProductGrouping);
    }
  }, [data]);

  useEffect(() => {
    if (!lpToCfEntityGrouping) {
      setMetadata(null);
    }
    const loanProductIds = lpToCfEntityGrouping.loanProducts.map(
      ({ id }) => id,
    );
    if (loanProductIds.length < 1) {
      setMetadata(null);
    }
  }, [lpToCfEntityGrouping, setMetadata]);

  const handleSetStatus = useCallback(
    (newStatus: Metadata) => {
      try {
        if (!lpToCfEntityGrouping) {
          setMetadata(null);
          history.push('/assignments');
        } else {
          setMetadata(newStatus);
        }
      } catch (err: any) {
        if (err.message) {
          errorToastConfig.title = err.message;
        }
        errorToastConfig.title = 'Error setting pipeline run status';
        errorToastConfig.id = `error-pipeline-run-status-${uuidV4()}`;
        toast(errorToastConfig);
      }
    },
    [lpToCfEntityGrouping, toast, history],
  );

  const context = useMemo(() => {
    return {
      metadata,
      setMetadata: handleSetStatus,
    };
  }, [metadata, handleSetStatus]);

  if (!context) {
    return renderLoading;
  }
  return (
    <LatestAssignmentPipelineRunMetadataForLpGroupingContext.Provider
      value={context}
    >
      {children}
    </LatestAssignmentPipelineRunMetadataForLpGroupingContext.Provider>
  );
};
